import React from 'react';
import { IconButton, Tooltip } from '@material-ui/core';
import { DeleteOutline } from '@material-ui/icons';
import { api_auth, api_host } from '../../../../common/globals/constants';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useDynamicViewState } from '../../context/dynamic-view-context';
import { deleteDynamicPanel } from '../../../../services/dynamicPanels';

/**
 * @fileoverview Botón para la eliminación de paneles dinámicos.
 * @module DeleteDashboard
 */

/**
 * Componente DeleteDashboard.
 *
 * Renderiza un botón con icono para eliminar un panel completo.
 *
 * Reglas de negocio:
 * - No permite eliminar el panel que está actualmente activo/visualizándose.
 * - Requiere confirmación del usuario antes de proceder.
 * - Ejecuta un callback `onDeleteSuccess` tras una eliminación exitosa para actualizar la UI.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.onDeleteSuccess - Callback a ejecutar tras eliminar el panel exitosamente.
 * @param {string|number} props.panelId - ID del panel que se va a eliminar.
 *
 * @returns {React.ReactElement} Botón de eliminación con tooltip.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function DeleteDashboard({ onDeleteSuccess, panelId }) {
  const { t } = useTranslation();
  const state = useDynamicViewState();

  /**
   * Manejador del evento de eliminación.
   *
   * 1. Verifica si el panel es el activo (no permitido).
   * 2. Solicita confirmación al usuario.
   * 3. Llama al servicio de eliminación.
   * 4. Notifica el resultado y ejecuta el callback de éxito.
   */
  async function handleDeletePanel() {
    if (state.activeLayoutId === panelId) {
      toast(t('dynamicPanel.deleteDashboard.cannotDeleteActive'), { type: 'error' });
      return;
    }
    const confirm = window.confirm(
      t('dynamicPanel.deleteDashboard.confirmDelete')
    );
    if (!confirm) return;
    try {
      await deleteDynamicPanel(panelId);
      toast(t('dynamicPanel.deleteDashboard.deleteSuccess'), { type: 'success' });
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('Error deleting panel:', error);
    }
  }

  return (
    <Tooltip title={t('dynamicPanel.deleteDashboard.tooltip')}>
      <IconButton onClick={handleDeletePanel}>
        <DeleteOutline style={{ color: 'red' }} />
      </IconButton>
    </Tooltip>
  );
}
